import React, { useCallback, useMemo, useRef, useState } from 'react';
import './DesignBoard.css';

const NODE_WIDTH = 200;
const NODE_HEIGHT = 52;
const GRID_STEP = 240;
const GRID_OFFSET = 24;

function getDefaultPosition(tempId, index, taskMap) {
  const task = taskMap[tempId];
  if (!task) return { x: (index % 4) * GRID_STEP + GRID_OFFSET, y: Math.floor(index / 4) * (GRID_STEP * 0.6) + GRID_OFFSET };
  if (task.type === 'UI_PARENT') {
    const parentIndex = task.parentIndex ?? 0;
    return { x: parentIndex * GRID_STEP + GRID_OFFSET, y: GRID_OFFSET };
  }
  const parent = task.parentTempId ? taskMap[task.parentTempId] : null;
  const siblingIndex = task.siblingIndex ?? 0;
  if (parent) {
    const px = (parent.layoutIndex ?? 0) * GRID_STEP + GRID_OFFSET;
    return { x: px + 20, y: GRID_OFFSET + NODE_HEIGHT + 12 + siblingIndex * (NODE_HEIGHT + 8) };
  }
  return { x: (index % 4) * GRID_STEP + GRID_OFFSET, y: Math.floor(index / 4) * (GRID_STEP * 0.6) + GRID_OFFSET };
}

export default function DesignBoard({
  tasks,
  layout,
  onLayoutChange,
  onTasksChange,
  onSelectTask,
  selectedTaskTempId,
  onOpenDrawer
}) {
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dropTarget, setDropTarget] = useState(null);
  const boardRef = useRef(null);

  const taskMap = useMemo(() => {
    const m = {};
    const parents = tasks.filter((t) => t.type === 'UI_PARENT');
    parents.forEach((p, pi) => {
      m[p.temp_id] = { ...p, parentIndex: pi, layoutIndex: pi };
    });
    tasks.forEach((t) => {
      if (t.type === 'UI_PARENT') return;
      const parent = tasks.find((p) => p.children && p.children.includes(t.temp_id));
      if (parent) {
        const siblings = (parent.children || []).filter((id) => id !== t.temp_id);
        const siblingIndex = (parent.children || []).indexOf(t.temp_id);
        m[t.temp_id] = { ...t, parentTempId: parent.temp_id, siblingIndex, parent };
      } else {
        m[t.temp_id] = t;
      }
    });
    return m;
  }, [tasks]);

  const positions = useMemo(() => {
    const pos = {};
    tasks.forEach((t, i) => {
      pos[t.temp_id] = layout[t.temp_id] || getDefaultPosition(t.temp_id, i, taskMap);
    });
    return pos;
  }, [tasks, layout, taskMap]);

  const dependencyEdges = useMemo(() => {
    const edges = [];
    tasks.forEach((t) => {
      (t.dependencies || []).forEach((depId) => {
        if (tasks.some((x) => x.temp_id === depId)) {
          edges.push({ from: t.temp_id, to: depId });
        }
      });
    });
    return edges;
  }, [tasks]);

  const handleNodeClick = useCallback(
    (task) => {
      onSelectTask(task.temp_id);
      if (onOpenDrawer) onOpenDrawer();
    },
    [onSelectTask, onOpenDrawer]
  );

  const handleDragStart = useCallback(
    (e, task) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', task.temp_id);
      e.dataTransfer.setData('application/json', JSON.stringify({ temp_id: task.temp_id, type: task.type }));
      setDragging(task.temp_id);
      const rect = e.currentTarget.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    },
    []
  );

  const handleDragEnd = useCallback(() => {
    setDragging(null);
    setDropTarget(null);
  }, []);

  const handleDragOver = useCallback((e, targetTask) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (targetTask && targetTask.type === 'UI_PARENT') {
      setDropTarget(targetTask.temp_id);
    } else {
      setDropTarget(null);
    }
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleDrop = useCallback(
    (e, targetTask) => {
      e.preventDefault();
      setDropTarget(null);
      const raw = e.dataTransfer.getData('application/json');
      const fromId = e.dataTransfer.getData('text/plain');
      if (!fromId) return;

      if (targetTask && targetTask.type === 'UI_PARENT' && fromId !== targetTask.temp_id) {
        let parsed = null;
        try {
          parsed = raw ? JSON.parse(raw) : { temp_id: fromId };
        } catch (_) {
          parsed = { temp_id: fromId };
        }
        const childId = parsed.temp_id || fromId;
        const child = tasks.find((t) => t.temp_id === childId);
        if (child && child.type !== 'UI_PARENT') {
          const oldParent = tasks.find((p) => p.children && p.children.includes(childId));
          if (oldParent && oldParent.temp_id !== targetTask.temp_id) {
            const updated = tasks.map((t) => {
              if (t.temp_id === oldParent.temp_id) {
                return { ...t, children: (t.children || []).filter((id) => id !== childId) };
              }
              if (t.temp_id === targetTask.temp_id) {
                return { ...t, children: [...(t.children || []), childId] };
              }
              return t;
            });
            if (onTasksChange) onTasksChange(updated);
          }
        }
        setDragging(null);
        return;
      }

      const boardRect = boardRef.current?.getBoundingClientRect();
      const baseX = boardRect ? e.clientX - boardRect.left : e.clientX - e.currentTarget.getBoundingClientRect().left;
      const baseY = boardRect ? e.clientY - boardRect.top : e.clientY - e.currentTarget.getBoundingClientRect().top;
      const newX = Math.max(0, baseX - dragOffset.x);
      const newY = Math.max(0, baseY - dragOffset.y);
      const newLayout = { ...layout, [fromId]: { x: newX, y: newY } };
      if (onLayoutChange) onLayoutChange(newLayout);
      setDragging(null);
    },
    [layout, tasks, dragOffset, onLayoutChange, onTasksChange]
  );

  const handleBoardDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDropTarget(null);
      const fromId = e.dataTransfer.getData('text/plain');
      if (!fromId || !onLayoutChange) return;
      const board = e.currentTarget;
      const rect = board.getBoundingClientRect();
      const newX = Math.max(0, e.clientX - rect.left - dragOffset.x);
      const newY = Math.max(0, e.clientY - rect.top - dragOffset.y);
      const newLayout = { ...layout, [fromId]: { x: newX, y: newY } };
      onLayoutChange(newLayout);
      setDragging(null);
    },
    [layout, dragOffset, onLayoutChange]
  );

  const handleBoardDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(null);
  }, []);

  if (!tasks || !tasks.length) return null;

  const allPositions = { ...positions };
  if (dragging && allPositions[dragging]) {
    allPositions[dragging] = { ...allPositions[dragging] };
  }

  const positionsList = Object.values(allPositions);
  const boardWidth = positionsList.length
    ? Math.max(800, Math.max(...positionsList.map((p) => p.x + NODE_WIDTH)) + GRID_OFFSET)
    : 800;
  const boardHeight = positionsList.length
    ? Math.max(500, Math.max(...positionsList.map((p) => p.y + NODE_HEIGHT)) + GRID_OFFSET)
    : 500;

  return (
    <div
      ref={boardRef}
      className="fds-design-board"
      onDragOver={handleBoardDragOver}
      onDrop={handleBoardDrop}
      style={{ position: 'relative', width: '100%', minHeight: boardHeight }}
    >
      <svg
        className="fds-board-edges"
        width={boardWidth}
        height={boardHeight}
        style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}
      >
        {dependencyEdges.map(({ from, to }) => {
          const a = allPositions[from];
          const b = allPositions[to];
          if (!a || !b) return null;
          const x1 = a.x + NODE_WIDTH / 2;
          const y1 = a.y + NODE_HEIGHT / 2;
          const x2 = b.x + NODE_WIDTH / 2;
          const y2 = b.y + NODE_HEIGHT / 2;
          return (
            <line
              key={`${from}-${to}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#adb5bd"
              strokeWidth={1.5}
              strokeDasharray="4 2"
              markerEnd="url(#arrow)"
            />
          );
        })}
        <defs>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0, 8 4, 0 8" fill="#adb5bd" />
          </marker>
        </defs>
      </svg>

      <div className="fds-board-nodes" style={{ position: 'relative', width: boardWidth, minHeight: boardHeight }}>
        {tasks.map((task) => {
          const pos = allPositions[task.temp_id] || { x: 0, y: 0 };
          const isParent = task.type === 'UI_PARENT';
          const isDropTarget = dropTarget === task.temp_id;
          return (
            <div
              key={task.temp_id}
              className={`fds-node-wrapper ${isParent ? 'fds-node-parent' : 'fds-node-child'} ${selectedTaskTempId === task.temp_id ? 'selected' : ''} ${isDropTarget ? 'fds-drop-target' : ''}`}
              style={{
                position: 'absolute',
                left: pos.x,
                top: pos.y,
                width: NODE_WIDTH,
                height: NODE_HEIGHT
              }}
              draggable
              onDragStart={(e) => handleDragStart(e, task)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, task)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, task)}
              onClick={() => handleNodeClick(task)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleNodeClick(task)}
            >
              <div className="fds-node">
                <span className="fds-node-id">{task.temp_id}</span>
                <span className="fds-node-title">{task.title}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
